import type { PhaseLabel } from '../../helpers/phase-labels/phase-labels.type';
import type { TransformedTarget } from '../targets-client/targets-client.type';

enum ModuleType {
  Dashboard = 'dashboard',
  Financial = 'financial',
  Initiative = 'initiative',
  Process = 'process',
  Risk = 'risk',
  Adherence = 'adherence',
  ProcessAnalysis = 'process_analysis',
  WorkPeriod = 'work_period',
}

type MeasureMetadata = {
  config_id?: string;
  measure_name: Measure;
  measure_title: string;
  measure_group: string;
  measure_description: string;
  measure_purpose: string;
  measure_units: MeasureUnits;
  is_zero_valid: boolean;
  is_custom?: boolean;
  targets?: TransformedTarget[];
};

type MeasureDetail = {
  name: string;
  title: string;
  group: string;
  description: string;
  units: string | null;
  purpose: string;
  is_zero_valid: boolean;
  is_custom: boolean;
};

enum MeasureUnits {
  Hours = 'Hours',
  Days = 'Days',
  Number = 'Number',
  Tasks = 'Tasks',
  Points = 'Points',
  Percentage = 'Percentage',
  Text = 'Text',
}

enum WorkPeriodTense {
  Future = 'future',
  Past = 'past',
  Present = 'present',
}

enum Measure {
  AuditLog = 'audit_log',
  BulkStatusChanges = 'bulk_status_changes',
  Burns = 'burns',
  Complexity = 'complexity',
  Custom = 'custom',
  CycleTime = 'cycle_time',
  CycleTimeInconsistency = 'cycle_time_inconsistency',
  DeclinedChangeRequests = 'declined_change_requests',
  FlowByPace = 'flow_by_pace',
  FlowByPhase = 'flow_by_phase',
  HealthScores = 'health_scores',
  HistoricalBurns = 'historical_burns',
  InitiativesFocus = 'initiatives_focus',
  Independence = 'independence',
  InitialCommittedPoints = 'initial_committed_points',
  InitialCommittedTasks = 'initial_committed_tasks',
  LeadTime = 'lead_time',
  MissingDays = 'missing_days',
  MultiplePointChangesPerTask = 'multiple_point_changes_per_task',
  Quality = 'quality',
  PointsAcceptanceRate = 'points_acceptance_rate',
  ReactionTime = 'reaction_time',
  Readiness = 'readiness',
  ReviewTime = 'review_time',
  ScopeCreep = 'scope_creep',
  StaleTasks = 'stale_tasks',
  Strategy = 'strategy',
  TaskFlow = 'task_flow',
  TasksAcceptanceRate = 'tasks_acceptance_rate',
  TasksMovingBackwards = 'tasks_moving_backwards',
  TechDebt = 'tech_debt',
  Throughput = 'throughput',
  UntestedTasks = 'untested_tasks',
  Velocity = 'velocity',
  WorkPlanInconsistency = 'work_plan_inconsistency',
  WorkloadDistributionCapacity = 'workload_distribution_capacity',
}

enum Transformer {
  CumulativeSingularValue = 'cumulative_singular_value',
  LastValueIndexed = 'last_value_indexed',
  SingleFractionalValue = 'single_fractional_value',
  SingleLastNonNull = 'single_last_not_null',
  TimeAllocationValuesOnly = 'time_allocation_values_only',
  TimeSeriesEarliestNotNull = 'time_series_earliest_not_null',
  TimeSeriesMostRecentNotNull = 'time_series_most_recent_not_null',
  ReturnFirstMetricOnly = 'return_first_metric_only',
}

enum EntityType {
  Portfolio = 'portfolio',
  Project = 'project',
  Subproject = 'subproject',
}

enum TimeAllocationType {
  Daily = 'daily',
  Weekly = 'weekly',
  BiWeekly = 'biweekly',
  Monthly = 'monthly',
  Quarterly = 'quarterly',
}

type TimeAllocation = {
  id: string;
  name: string;
  start: string;
  end: string;
};

type WorkPeriod = {
  id: string;
  name: string;
  parent: string | null;
  subproject: string;
  external_id: string;
  start_date: string;
  end_date: string;
  time_tense: WorkPeriodTense;
  budget: number;
  ai_insights?: string | null;
};

type BulkUpdateWorkPeriodsPayload = {
  id: string;
  budget: number | null;
};

type GetAvailableMeasuresPayload = {
  scope: ModuleType[];
  organization_id?: string;
  portfolio_id?: string;
  project_id?: string;
  subproject_id?: string;
  work_period_id?: string;
};

type GetTimeAllocationsPayload = {
  start_date: string;
  end_date: string;
  time_allocation_type: TimeAllocationType;
  organization_id?: string;
};

type AvailableMeasuresResponse = {
  [key in ModuleType]: {
    measures: MeasureMetadata[];
  };
};

type GetWorkPeriodsPayload = {
  subproject_id: string | null;
  parent?: string;
  external_id?: string;
  time_span?: string;
  time_tense?: string;
  organization_id?: string;
};

type GetWorkPeriodsMeasurePayload = {
  work_period_id?: string;
  subproject_id?: string;
  project_id?: string;
  portfolio_id?: string;
  initiative_id?: string;
  epic_id?: string;
  page_id?: string;
  start_date?: string;
  end_date?: string;
  measure_name: Measure[] | string[];
  time_allocation_type?: TimeAllocationType;
  transformer?: string;
  force_recalculation?: boolean;
  measure_filter?: string;
  is_custom?: boolean;
  enabled?: boolean;
  staleTime?: number;
  module_type?: ModuleType;
  use_groups?: boolean;
  organization_id?: string;
  task_type?: string;
};

type GetCustomSprintMeasurePayload = {
  subproject_id: string;
  measure_name: string;
  n_sprints: number;
  include_current_sprint?: boolean;
  transformer?: string;
  force_recalculation?: boolean;
  module_type: ModuleType;
  organization_id?: string;
};

type UseWorkPeriodsMeasurePayloadMultiple = {
  entity: EntityType;
  ids: string[];
  start_date: string;
  end_date: string;
  measure_name: Measure[] | string[];
  time_allocation_type?: TimeAllocationType;
  transformer?: string;
  force_recalculation?: boolean;
  is_custom?: boolean;
  module_type?: ModuleType;
  organization_id?: string;
  task_type?: string;
};

type UseWorkPeriodsMeasurePayloadSingular = {
  subproject_id?: string;
  work_period_id?: string;
  project_id?: string;
  portfolio_id?: string;
  initiative_id?: string;
  epic_id?: string;
  start_date?: string;
  end_date?: string;
  measure_name: Measure[];
  time_allocation_type?: TimeAllocationType;
  transformer?: string;
  force_recalculation?: boolean;
  measure_filter?: string;
  queryKey?: readonly unknown[];
  enabled?: boolean;
  staleTime?: number;
  onError?: (error: Error) => void;
  is_custom?: boolean;
  module_type?: ModuleType;
  use_groups?: boolean;
  organization_id?: string;
};

type FlowByPhaseData = {
  tasks_wip: number;
  points_wip: number;
  tasks_done: number;
  tasks_test: number;
  points_done: number;
  points_test: number;
  tasks_review: number;
  points_review: number;
  tasks_backlog: number;
  tasks_blocked: number;
  points_backlog: number;
  points_blocked: number;
  tasks_deployable: number;
  points_deployable: number;
};

type FlowByPhaseDataOverTime = Record<string, FlowByPhaseData>;

enum PaceStatus {
  OnTrack = 'On Track',
  AtRisk = 'At Risk',
  OffTrack = 'Off Track',
}

type FlowByPaceData = {
  date: string;
  points_complete: number;
  points_doing: number;
  points_ideal: number;
  points_original_plan: number;
  points_remaining: number;
  points_status: PaceStatus;
  points_total: number;
  tasks_complete: number;
  tasks_doing: number;
  tasks_ideal: number;
  tasks_original_plan: number;
  tasks_remaining: number;
  tasks_status: PaceStatus;
  tasks_total: number;
  weekend_or_holiday: boolean;
};

type FlowByPaceDataOverTime = Record<string, FlowByPaceData>;

type HealthScoreData = {
  health_scores: {
    score_by_tasks: number;
    score_by_points: number;
  };
};

type BurnsData = {
  date: string;
  tasks_total: number;
  tasks_complete: number;
  points_total: number;
  points_complete: number;
  weekend_or_holiday: boolean;
};

type BurnsDataOverTime = Record<string, BurnsData>;

type HistoricalBurnsData = {
  tasks: { average: number; y: number; y0: number };
  points: { average: number; y: number; y0: number };
};

type HistoricalBurnsDataOverTime = Record<string, HistoricalBurnsData>;

type MeasureValuesOverTime = Record<string, number>;
type MeasureDataResponse = Record<Measure, MeasureValuesOverTime> & {
  /**
   * Per-workflow display labels for the seven canonical phase keys. Emitted
   * by the backend at the top level of phase-aware measure responses
   * (`flow_by_phase`, `burns_and_flows`, `bloomcast_by_phase`, `task_flow`)
   * and forwarded verbatim through `sanitizeMeasureDataResponse`.
   */
  phases?: PhaseLabel[];
};
type MeasureDataByEntity = Record<string, MeasureDataResponse>;
type CustomMeasureDataResponse = Record<string, MeasureValuesOverTime>;

type CustomSprintMeasureData = {
  measures: CustomMeasureDataResponse;
  work_period: {
    name: string;
    start_date: string;
    end_date: string;
  };
};

type CustomSprintMeasureDataResponse = Record<string, CustomSprintMeasureData>;

type WorkPeriodGoal = {
  id: string;
  work_period: string;
  description: string;
  completed: boolean;
  source: 'bloomfilter' | 'jira';
  metadata_updated_at: string;
};

/**
 * One labeled row of the work-period task-type breakdown. `raw_type` is the
 * stored normalized type key (the null bucket arrives as "Unknown");
 * `raw_type_label` carries the verbatim external casing, falling back to
 * equal `raw_type` when no label has been synced.
 */
interface TaskTypeBreakdownEntry {
  raw_type: string;
  raw_type_label: string;
  count: number;
}

interface TaskTypeBreakdownResponse {
  types: Record<string, number>;
  total: number;
  /**
   * Labeled rows mirroring `types`, added alongside the legacy keys.
   * Optional because older backends omit it — consumers must keep the
   * `types`-based path as fallback.
   */
  type_breakdown?: TaskTypeBreakdownEntry[];
}

type WorkPeriodGoalForm = Pick<WorkPeriodGoal, 'description' | 'completed' | 'work_period' | 'source'>;
type CreateWorkPeriodGoalPayload = Omit<WorkPeriodGoal, 'id'>;
type UpdateWorkPeriodGoalPayload = WorkPeriodGoal;

export { EntityType, Measure, MeasureUnits, ModuleType, PaceStatus, TimeAllocationType, Transformer, WorkPeriodTense };

export type {
  AvailableMeasuresResponse,
  BulkUpdateWorkPeriodsPayload,
  BurnsData,
  BurnsDataOverTime,
  CreateWorkPeriodGoalPayload,
  CustomSprintMeasureDataResponse,
  FlowByPaceData,
  FlowByPaceDataOverTime,
  FlowByPhaseData,
  FlowByPhaseDataOverTime,
  GetAvailableMeasuresPayload,
  GetCustomSprintMeasurePayload,
  GetTimeAllocationsPayload,
  GetWorkPeriodsMeasurePayload,
  GetWorkPeriodsPayload,
  HealthScoreData,
  HistoricalBurnsData,
  HistoricalBurnsDataOverTime,
  MeasureDataByEntity,
  MeasureDataResponse,
  MeasureDetail,
  MeasureMetadata,
  MeasureValuesOverTime,
  TaskTypeBreakdownEntry,
  TaskTypeBreakdownResponse,
  TimeAllocation,
  UpdateWorkPeriodGoalPayload,
  UseWorkPeriodsMeasurePayloadMultiple,
  UseWorkPeriodsMeasurePayloadSingular,
  WorkPeriod,
  WorkPeriodGoal,
  WorkPeriodGoalForm,
};
